<script setup>
/**
 * Centered modal that hosts the campaign create forms.
 *
 * The forms embed the rich-text editor, whose "add link" prompt is a native
 * `<dialog>` opened on top of this panel. Both close paths below stay out of its
 * way: a backdrop click only counts when the click target is the backdrop
 * element itself, and Escape is ignored while any native dialog is open above us.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

defineProps({
  title: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const close = () => emit('close');

const handleBackdropClick = event => {
  if (event.target === event.currentTarget) close();
};

const panelRef = ref(null);

const handleKeydown = event => {
  if (event.key !== 'Escape') return;
  if (document.querySelector('dialog[open]')) return;

  // The innermost overlay gets the key first: closing the panel while a
  // combobox dropdown is open would throw away a half-filled campaign. A second
  // Escape, with nothing layered on top, closes the panel.
  const openCombobox = panelRef.value?.querySelector('[data-combobox-open]');
  if (openCombobox) {
    openCombobox.dispatchEvent(new CustomEvent('combobox-escape'));
    return;
  }

  close();
};

onMounted(() => document.addEventListener('keydown', handleKeydown));
onBeforeUnmount(() => document.removeEventListener('keydown', handleKeydown));
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-n-alpha-black1 backdrop-blur-[4px]"
      @click="handleBackdropClick"
    >
      <div
        ref="panelRef"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        class="flex flex-col w-full max-w-[28rem] min-w-0 gap-6 p-6 border shadow-xl rounded-xl border-n-weak bg-n-alpha-3 backdrop-blur-[100px] max-h-[85vh] overflow-y-auto"
      >
        <div class="flex items-start justify-between flex-shrink-0 gap-3">
          <h3 class="text-heading-2 text-n-slate-12">
            {{ title }}
          </h3>
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="xs"
            :aria-label="t('GENERAL.CLOSE')"
            @click="close"
          />
        </div>
        <slot />
      </div>
    </div>
  </TeleportWithDirection>
</template>
